import * as PIXI from 'pixi.js';
import Point from '../graphics/Point';

/**
 * A modified modulus operation which always returns a result with the same sign as the base.
 * Useful for circular arithmetic, such as converting negative angles to equivalent positive ones.
 */
function circularModulus(num, base) {
  return num - base * Math.floor(num / base);
}

function normalizeRadians(radians) {
  return circularModulus(radians, 2 * Math.PI);
}

export default class Angle {
  _radians = 0;

  get radians() {
    return this._radians;
  }

  get degrees() {
    return (180 * this.radians) / Math.PI;
  }

  constructor(radians) {
    this._radians = normalizeRadians(radians);
  }

  /**
   * Add an angle to this one and return the sum as a new Angle
   *
   * @param angle {Angle}
   * @returns {Angle}
   */
  plus(angle) {
    return new Angle(this.radians + angle.radians);
  }

  /**
   * Subtract an angle from this one and return the difference as a new Angle
   *
   * @param angle {Angle}
   * @returns {Angle}
   */
  minus(angle) {
    return new Angle(this.radians - angle.radians);
  }

  /**
   * Get the difference between this angle and another one, regardless of direction.
   * Result is always between 0 and Math.PI radians.
   *
   * @param angle {Angle}
   * @returns {number}
   */
  absoluteDifference(angle) {
    return Math.abs(
      normalizeRadians(this.radians - angle.radians + Math.PI) - Math.PI
    );
  }

  /**
   * Determine whether this angle is within the arc described by two other angles
   *
   * @param start {Angle}
   * @param end {Angle}
   * @returns {boolean}
   */
  withinArc(start, end) {
    return end.minus(start).radians >= this.minus(start).radians;
  }

  /**
   * Given a line described by two points, calculate the point where this angle intersects that line
   *
   * @param start {Point}
   * @param end {Point}
   * @returns {Point}
   */
  project(start, end) {
    if (start.x === end.x && start.y === end.y) {
      return start;
    }

    const lineAngle = end.minus(start).angle;
    const relativeAngle = this.minus(lineAngle).plus(new Angle(Math.PI / 2));

    const rotation = new PIXI.Matrix().rotate(lineAngle.radians);
    const lineDistance = rotation.applyInverse(start).y;

    if (Math.round(lineDistance) === 0) {
      // projecting onto line that runs through origin, pick the endpoint that most closely matches this angle
      if (
        this.absoluteDifference(start.angle) <
        this.absoluteDifference(end.angle)
      ) {
        return start;
      } else {
        return end;
      }
    }

    const relativeProjection = new Point(
      lineDistance * Math.tan(relativeAngle.radians),
      lineDistance
    );
    const { x, y } = rotation.apply(relativeProjection);

    return new Point(Math.round(x), Math.round(y));
  }
}
